import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Stack, LinearProgress, Chip,
  Divider, Alert,
} from '@mui/material';
import { Map as MapIcon, CloudUpload, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useApi } from '@/lib/apiFetch';

interface RegenResult {
  updated: number;
}

interface PushStatus {
  running: boolean;
  total: number;
  done: number;
  failed: number;
  errors: { katapult_id: string; error: string }[];
}

type Phase = 'idle' | 'regenerating' | 'pushing' | 'done' | 'error';

export default function PolygonSyncCard() {
  const apiFetch = useApi();

  const [phase, setPhase]           = useState<Phase>('idle');
  const [regenResult, setRegenResult] = useState<RegenResult | null>(null);
  const [pushStatus, setPushStatus]   = useState<PushStatus | null>(null);
  const [errorMsg, setErrorMsg]       = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const pollPushStatus = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch('/tools/push-polygons-to-arcgis/status');
        const data: PushStatus = await res.json();
        setPushStatus(data);
        if (!data.running) {
          stopPolling();
          setPhase('done');
        }
      } catch {
        stopPolling();
        setPhase('done');
      }
    }, 3000);
  }, [apiFetch]);

  const handleRun = useCallback(async () => {
    setPhase('regenerating');
    setRegenResult(null);
    setPushStatus(null);
    setErrorMsg(null);

    try {
      // Step 1: regenerate polygons in DB
      const regenRes = await apiFetch('/tools/regenerate-polygons', { method: 'POST' });
      if (!regenRes.ok) throw new Error(`Regenerate failed: ${regenRes.status}`);
      const regen: RegenResult = await regenRes.json();
      setRegenResult(regen);

      // Step 2: kick off background push to ArcGIS
      setPhase('pushing');
      const pushRes = await apiFetch('/tools/push-polygons-to-arcgis', { method: 'POST' });
      if (!pushRes.ok) throw new Error(`Push failed: ${pushRes.status}`);
      const push = await pushRes.json();
      if (!push.started) {
        setPhase('done');
        return;
      }

      // Step 3: poll until done
      pollPushStatus();
    } catch (e: any) {
      setErrorMsg(e.message ?? 'Unknown error');
      setPhase('error');
    }
  }, [apiFetch, pollPushStatus]);

  const reset = () => {
    stopPolling();
    setPhase('idle');
    setRegenResult(null);
    setPushStatus(null);
    setErrorMsg(null);
  };

  const pushPct = pushStatus && pushStatus.total > 0
    ? Math.round(((pushStatus.done + pushStatus.failed) / pushStatus.total) * 100)
    : 0;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" spacing={2} mb={2.5}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: 'rgba(129,140,248,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <MapIcon size={24} color="#818cf8" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2, mb: 0.5 }}>
            Job Polygon Sync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Regenerate job boundary polygons from aerial, slack span, overlash, and reference
            connections, then push to ArcGIS layer 3. Hold / Junk / Archived jobs are skipped.
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 2.5 }} />

      {/* Action button */}
      {phase === 'idle' && (
        <Button
          variant="contained"
          startIcon={<CloudUpload size={15} />}
          onClick={handleRun}
          sx={{
            fontSize: '0.82rem', px: 3, py: 1,
            bgcolor: 'accent.indigo', '&:hover': { bgcolor: '#4f46e5' },
          }}
        >
          Regenerate &amp; Push to ArcGIS
        </Button>
      )}

      {/* Regenerating progress */}
      {phase === 'regenerating' && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Clock size={14} color="#a5b4fc" />
            <Typography sx={{ fontSize: '0.78rem', color: '#a5b4fc' }}>
              Regenerating polygons in database…
            </Typography>
          </Stack>
          <LinearProgress sx={{
            borderRadius: 4, height: 5,
            bgcolor: 'rgba(99,102,241,0.15)',
            '& .MuiLinearProgress-bar': { bgcolor: 'accent.indigo' },
          }} />
        </Box>
      )}

      {/* Pushing progress */}
      {phase === 'pushing' && (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CloudUpload size={14} color="#a5b4fc" />
              <Typography sx={{ fontSize: '0.78rem', color: '#a5b4fc' }}>
                Pushing to ArcGIS…
              </Typography>
            </Stack>
            {pushStatus && (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                {pushStatus.done + pushStatus.failed} / {pushStatus.total}
              </Typography>
            )}
          </Stack>
          <LinearProgress
            variant={pushStatus ? 'determinate' : 'indeterminate'}
            value={pushPct}
            sx={{
              borderRadius: 4, height: 5,
              bgcolor: 'rgba(99,102,241,0.15)',
              '& .MuiLinearProgress-bar': { bgcolor: 'accent.indigoLight' },
            }}
          />
          {(pushStatus?.failed ?? 0) > 0 && (
            <Typography sx={{ fontSize: '0.68rem', color: 'warning.main', mt: 0.5 }}>
              {pushStatus!.failed} failed so far
            </Typography>
          )}
        </Box>
      )}

      {/* Results */}
      {(phase === 'done' || phase === 'error') && (
        <Box>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.78rem' }}>{errorMsg}</Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" mb={1.5}>
            {regenResult && (
              <Chip
                size="small"
                icon={<CheckCircle size={12} />}
                label={`${regenResult.updated} polygons regenerated`}
                sx={{ fontSize: '0.72rem', bgcolor: 'rgba(34,197,94,0.12)', color: '#4ade80', '& .MuiChip-icon': { color: '#4ade80' } }}
              />
            )}
            {pushStatus && (
              <Chip
                size="small"
                icon={<CloudUpload size={12} />}
                label={`${pushStatus.done} pushed to ArcGIS`}
                sx={{ fontSize: '0.72rem', bgcolor: 'rgba(129,140,248,0.12)', color: '#a5b4fc', '& .MuiChip-icon': { color: '#a5b4fc' } }}
              />
            )}
            {(pushStatus?.failed ?? 0) > 0 && (
              <Chip
                size="small"
                icon={<XCircle size={12} />}
                label={`${pushStatus!.failed} failed`}
                sx={{ fontSize: '0.72rem', bgcolor: 'rgba(239,68,68,0.12)', color: 'error.main', '& .MuiChip-icon': { color: 'error.main' } }}
              />
            )}
          </Stack>

          {(pushStatus?.errors ?? []).length > 0 && (
            <Box sx={{
              p: 1.5, mb: 1.5,
              bgcolor: 'rgba(239,68,68,0.06)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 1.5,
              maxHeight: 120, overflow: 'auto',
            }}>
              <Typography sx={{ fontSize: '0.68rem', color: 'error.main', fontWeight: 600, mb: 0.5 }}>
                Failed jobs:
              </Typography>
              {pushStatus!.errors.map((e, i) => (
                <Typography key={i} sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                  {e.katapult_id}: {e.error}
                </Typography>
              ))}
            </Box>
          )}

          <Button
            size="small" variant="outlined" onClick={reset}
            sx={{ fontSize: '0.72rem', color: 'text.secondary', borderColor: 'rgba(148,163,184,0.2)' }}
          >
            Reset
          </Button>
        </Box>
      )}
    </Box>
  );
}
